import { useState, useEffect, useCallback } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { DataGrid } from '@mui/x-data-grid'


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatIndexPeriod(index) {
    if (!index) return '—'
    const period = index.for_month != null
        ? `M${String(index.for_month).padStart(2, '0')}`
        : `T${index.for_quarter}`
    return `${index.name} ${index.for_year} ${period}`
}

function formatAmount(value) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value)
}

function formatDate(value) {
    if (!value) return '—'
    return new Date(value).toLocaleDateString('fr-FR')
}


// ---------------------------------------------------------------------------
// Column definitions
//
// MUI DataGrid v7: valueFormatter receives the cell value directly,
// not a { value } params object.
// ---------------------------------------------------------------------------
const COLUMNS = [
    {
        field:          'created_at',
        headerName:     'Date d\'indexation',
        flex:           1,
        valueFormatter: (value) => formatDate(value),
    },
    {
        field:          'valid_until',
        headerName:     'Valide jusqu\'au',
        flex:           1,
        valueFormatter: (value) => formatDate(value),
    },
    {
        field:          'price_index',
        headerName:     'Indice',
        flex:           1.5,
        sortable:       false,
        valueFormatter: (value) => formatIndexPeriod(value),
    },
    {
        field:      'index_value',
        headerName: 'Valeur de l\'indice',
        flex:       1,
        type:       'number',
    },
    {
        field:          'total',
        headerName:     'Loyer total',
        flex:           1,
        type:           'number',
        valueFormatter: (value) => formatAmount(value),
    },
]


// ---------------------------------------------------------------------------
// Component
//
// Props
//   supabase    – initialised Supabase client
//   propertyId  – UUID of the property (required; this component is never
//                 used without one)
//
// Each row represents one past indexation generation: a set of
// transactions_bucket rows that shared the same price_index_id and have
// all since expired (valid_until is not null).  The current (active)
// generation is excluded.
// ---------------------------------------------------------------------------
export default function IndexationsTable({ supabase, propertyId }) {

    const [rows,    setRows]    = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)


    // =========================================================================
    // Fetch, group by price_index_id, and build rows
    // =========================================================================
    const fetchIndexations = useCallback(async () => {
        setError(null)

        try {
            const today = new Date().toISOString().split('T')[0]

            // ── 1. Active lease for this property ────────────────────────────
            const { data: leaseData, error: leaseError } = await supabase
                .from('lease')
                .select('id')
                .eq('property_id', propertyId)
                .or(`end_date.is.null,end_date.gte.${today}`)
                .order('start_date', { ascending: false })
                .limit(1)
                .maybeSingle()

            if (leaseError) throw leaseError

            if (!leaseData) {
                setRows([])
                return
            }

            // ── 2. All buckets (active + expired) for this lease ─────────────
            const { data: bucketsData, error: bucketsError } = await supabase
                .from('transactions_bucket')
                .select(`
                    id,
                    default_value,
                    valid_until,
                    created_at,
                    price_index_id,
                    price_index:price_index_id (
                        name, value, for_year, for_quarter, for_month
                    )
                `)
                .eq('lease_id', leaseData.id)
                .order('created_at', { ascending: true })

            if (bucketsError) throw bucketsError

            // ── 3. Build generation map ──────────────────────────────────────
            //
            // apply_indexation() marks old buckets valid_until = yesterday and
            // creates new ones.  Each distinct price_index_id is one generation.
            // Buckets without a price_index reference (__none__) are excluded.
            const genMap = {}
            for (const b of bucketsData ?? []) {
                const key = b.price_index_id ?? '__none__'
                if (!genMap[key]) {
                    genMap[key] = {
                        price_index:  b.price_index,
                        created_at:   b.created_at,
                        valid_until:  b.valid_until,
                        total:        0,
                        hasActiveRow: false,
                    }
                }
                genMap[key].total += b.default_value ?? 0
                if (!b.valid_until) {
                    genMap[key].hasActiveRow = true
                } else if (
                    !genMap[key].hasActiveRow &&
                    (!genMap[key].valid_until || b.valid_until > genMap[key].valid_until)
                ) {
                    genMap[key].valid_until = b.valid_until
                }
            }

            // ── 4. Keep only expired generations with a known index ──────────
            const history = Object.entries(genMap)
                .filter(([, g]) => !g.hasActiveRow && g.price_index != null)
                .sort(([, a], [, b]) => new Date(b.created_at) - new Date(a.created_at))
                .map(([key, g]) => ({
                    id:          key,
                    created_at:  g.created_at,
                    valid_until: g.valid_until,
                    price_index: g.price_index,
                    index_value: g.price_index?.value ?? null,
                    total:       g.total,
                }))

            setRows(history)
        } catch (err) {
            setError(err.message ?? 'Erreur lors du chargement des ré-indexations.')
        } finally {
            setLoading(false)
        }
    }, [supabase, propertyId])


    // =========================================================================
    // Initial fetch + realtime subscription on transactions_bucket
    // =========================================================================
    useEffect(() => {
        if (!propertyId) return
        fetchIndexations()

        const channel = supabase
            .channel(`indexations-table-${propertyId}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'transactions_bucket' },
                fetchIndexations,
            )
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [supabase, propertyId, fetchIndexations])


    // =========================================================================
    // Render
    // =========================================================================
    return (
        <Box sx={{ width: '100%' }}>
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
                    {error}
                </Alert>
            )}

            {!error && (
                <DataGrid
                    autoHeight
                    loading={loading}
                    rows={rows}
                    columns={COLUMNS}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                    }
                    initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                    }}
                    pageSizeOptions={[10, 20, 50]}
                    density="compact"
                    disableRowSelectionOnClick
                />
            )}
        </Box>
    )
}